import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportPersons } from "~pages/Persons/PersonListPage/hooks/useExportPersons.ts";
import { useImportPersons } from "~pages/Persons/PersonListPage/hooks/useImportPersons.ts";
import type { CreatePerson, UpdatePersonPayload } from "~pages/Persons/types/upsertPerson.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreatePerson } from "./useCreatePerson.ts";
import { useDeletePerson } from "./useDeletePerson.ts";
import { useUpdatePerson } from "./useUpdatePerson.ts";

export const usePersonService = () => {
  const { mutateAsync: createPersonFn, isPending: isCreatePending } = useCreatePerson();
  const { mutateAsync: updatePersonFn, isPending: isUpdatePending } = useUpdatePerson();
  const { mutateAsync: deletePersonFn, isPending: isDeletePending } = useDeletePerson();
  const { mutateAsync: importPersonsFn, isPending: isImportPending } = useImportPersons();
  const { mutateAsync: exportPersonsFn, isPending: isExportPending } = useExportPersons();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.PERSON);

  const createPerson = (payload: CreatePerson) =>
    create(() => createPersonFn(payload), {
      successMessageId: "person.add.success",
    });

  const updatePerson = (personId: string, payload: UpdatePersonPayload) =>
    update(() => updatePersonFn({ ...payload, personId }), {
      successMessageId: "person.update.success",
    });

  const deletePerson = async (personId: string) =>
    Boolean(await remove(() => deletePersonFn(personId), { successMessageId: "person.delete.success" }));

  const importPersons = async (fileName: string) =>
    executeOrThrow(() => importPersonsFn(fileName), {
      successMessageId: "persons.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportPersons = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportPersonsFn(fieldNames), {
      successMessageId: "persons.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    createPerson,
    isCreatePending,
    updatePerson,
    isUpdatePending,
    deletePerson,
    isDeletePending,
    importPersons,
    isImportPending,
    exportPersons,
    isExportPending,
  };
};
